import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Slider,
  styled,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { Controller } from 'react-hook-form';
import CustomSelect from '../../../../components/form/CustomSelect';
import { Rules } from '../../../../helpers/RHFRules';

const getGeneralStyles = (theme) => ({
  '& .MuiOutlinedInput-root': {
    position: 'relative',
    borderRadius: '10px',
    boxShadow: '1px 5px 5px #d7d7d7',
    '& svg': {
      color: theme.backOffice.contracts.tableProducts.colorIcon,
    },
  },
});

const MyCustomSelect = styled(CustomSelect)(({ theme, height = 36, paddingBottom = 24 }) => ({
  ...getGeneralStyles(theme, height),
  paddingBottom,
}));

const MyCheckbox = styled(Checkbox)(({ theme }) => ({
  '&.Mui-checked': {
    color: theme.backOffice.contracts.tableProducts.colorIcon,
  },
}));

const MyFormControlLabel = styled(FormControlLabel)(() => ({
  '& .MuiFormControlLabel-label': {
    fontSize: 16,
    color: '#575756',
    fontWeight: 600,
  },
}));

const MyTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'color'
    && prop !== 'fontWeight'
    && prop !== 'fontSize'
    && prop !== 'paddingBottom'
    && prop !== 'textAlign',
})(({
  color = '#000000',
  fontWeight = 900,
  fontSize = 21,
  paddingBottom = 0,
  textAlign = 'left',
}) => ({
  fontFamily: 'Mulish, sans-serif',
  fontSize,
  fontWeight,
  color,
  paddingBottom,
  textAlign,
}));

function AlertPeriodicity(props) {
  const { formActions, alertPeriodicityParams } = props;
  const [defaultAlertPeriodicityParams, setDefaultAlertPeriodicityParams] = useState({
    consumptionPercentageAlert: 0,
    deliveryFrecuency: [],
    alertDeliveryTime: [],
    alertDeliveryDays: [],
  });
  const theme = useTheme();
  const weekDays = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

  useEffect(() => {
    if (alertPeriodicityParams) {
      setDefaultAlertPeriodicityParams({
        deliveryFrecuency: alertPeriodicityParams.deliveryFrecuency,
        alertDeliveryTime: alertPeriodicityParams.alertDeliveryTime,
        alertDeliveryDays: alertPeriodicityParams.alertDeliveryDays,
      });
    }
  }, [alertPeriodicityParams, formActions]);

  useEffect(() => {
    const userAlertOptions = formActions?.valuesInForm?.userAlertOptions;
    if (userAlertOptions && Object.keys(alertPeriodicityParams).length > 0) {
      const deliveryTime = alertPeriodicityParams.alertDeliveryTime.find(
        (el) => el.value === userAlertOptions?.deliveryTime,
      )?.value;
      const deliveryFrecuency = alertPeriodicityParams.deliveryFrecuency.find(
        (el) => el.value === userAlertOptions?.deliveryFrecuency,
      )?.value;
      const notValidate = { shouldDirty: false, shouldValidate: false };
      formActions.setValueForm('alertDeliveryTime', deliveryTime, notValidate);
      formActions.setValueForm('percentageSlider', userAlertOptions?.consumptionPercentageAlert, notValidate);
      formActions.setValueForm('deliveryFrecuency', deliveryFrecuency, notValidate);
      formActions.setValueForm('selectedDays', userAlertOptions.alertDeliveryDays, notValidate);
    }
  }, [formActions, alertPeriodicityParams]);

  const changeObjectInForm = (keyForm, objectKey, keyName, dataToReplace) => {
    const currentObjectInfo = formActions.valuesInForm[objectKey];
    currentObjectInfo[keyName] = dataToReplace;
    formActions.setValuesInForm((prevData) => ({
      ...prevData,
      [objectKey]: currentObjectInfo,
    }));
    formActions.setValueForm(keyForm, dataToReplace);
  };

  return (
    <Grid container>
      <Grid item xs={6}>
        <MyTypography
          color={theme.backOffice.contracts.titleForm.color}
          paddingBottom={32}
        >
          Periodicidad de alertas
        </MyTypography>
        <MyCustomSelect
          label="Enviar alertas de vencimiento"
          name="alertDeliveryTime"
          options={defaultAlertPeriodicityParams?.alertDeliveryTime ?? []}
          control={formActions?.controlForm}
          required
          rule={Rules.required}
          error={formActions?.errorsForm}
          styleFromHelperText={{ style: { marginRight: 10 } }}
          inputProps={{ onChange: (e) => changeObjectInForm('alertDeliveryTime', 'userAlertOptions', 'deliveryTime', e.target.value) }}
        />
        <Box display="flex" alignItems="center" mb={2}>
          <MyTypography
            color="#575756"
            fontWeight={700}
            fontSize={14}
            paddingBottom={0}
            style={{ marginRight: 4 }}
          >
            Alerta de porcentaje de consumo de bolsa
          </MyTypography>
          <Tooltip title="Hace referencia al porcentaje de consumo de la bolsa donde quieres dar inicio al envío de alertas">
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
        </Box>
        <Controller
          name="percentageSlider"
          control={formActions?.controlForm}
          defaultValue={defaultAlertPeriodicityParams?.consumptionPercentageAlert ?? 1}
          /* inputProps={{ onChange: (e) => changeFormValue(e, 'percentageSlider') }} */
          render={(field) => (
            <Slider
              value={field.value}
              min={1}
              style={{ color: theme.backOffice.contracts.tableProducts.colorIcon }}
              onChange={(_, value) => {
                field.onChange(value);
                changeObjectInForm('percentageSlider', 'userAlertOptions', 'consumptionPercentageAlert', value);
              }}
              onBlur={field.onBlur}
              valueLabelDisplay="auto"
              aria-label="Default"
            />
          )}
        />
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <MyTypography
            color="#575756"
            fontWeight={700}
            fontSize={14}
            paddingBottom={24}
          >
            0%
          </MyTypography>
          <MyTypography
            color="#575756"
            fontWeight={700}
            fontSize={14}
            paddingBottom={24}
            textAlign="right"
          >
            100%
          </MyTypography>
        </Box>
        <MyCustomSelect
          label="Periodicidad de envío"
          name="deliveryFrecuency"
          options={defaultAlertPeriodicityParams.deliveryFrecuency ?? []}
          control={formActions?.controlForm}
          required
          rule={Rules.required}
          error={formActions?.errorsForm}
          styleFromHelperText={{ style: { marginRight: 10 } }}
          paddingBottom={32}
          inputProps={{ onChange: (e) => changeObjectInForm('deliveryFrecuency', 'userAlertOptions', 'deliveryFrecuency', e.target.value) }}
        />
        {(formActions.getValuesForm('deliveryFrecuency') === 'S') && (
          <>
            <MyTypography
              color="#575756"
              fontWeight={800}
              fontSize={15}
              paddingBottom={16}
              textAlign="lefth"
            >
              Días de envío a la semana
            </MyTypography>
            <Controller
              name="selectedDays"
              control={formActions?.controlForm}
              defaultValue={[]}
              rules={{
                validate: (value) => value.length > 0 || 'Debes seleccionar al menos un día',
              }}
              render={(field) => {
                const { value, onChange } = field;

                const handleToggle = (day) => {
                  const updated = value.includes(day)
                    ? value.filter((d) => d !== day)
                    : [...value, day];
                  onChange(updated);
                  changeObjectInForm('selectedDays', 'userAlertOptions', 'alertDeliveryDays', updated);
                };
                return (
                  <Grid container spacing={1}>
                    {weekDays.map((day) => (
                      <Grid item xs={12} key={day}>
                        <MyFormControlLabel
                          control={(
                            <MyCheckbox
                              checked={value.includes(day)}
                              onChange={() => handleToggle(day)}
                            />
                          )}
                          label={day}
                        />
                      </Grid>
                    ))}
                  </Grid>
                );
              }}
            />
          </>
        )}
        {formActions?.errorsForm?.selectedDays && (
          <Typography color="error" sx={{ mt: 1 }}>
            {formActions.errorsForm.selectedDays.message}
          </Typography>
        )}
      </Grid>
      <Grid item xs={6}>
        <></>
      </Grid>
    </Grid>
  );
}

AlertPeriodicity.propTypes = {};

export default AlertPeriodicity;
